import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutGrid,
  CircleHelp,
  Download,
  Settings,
  Sun,
  Moon,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  FileText,
} from 'lucide-react'
import { useAppStore } from '@/stores/appStore'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/lib/utils'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse?: () => void
  /**
   * When true, hide the collapse toggle. The mobile drawer reuses this
   * component but doesn't need the toggle (the drawer itself is the toggle).
   */
  hideCollapseToggle?: boolean
}

interface NavEntry {
  to: string
  label: string
  icon: typeof LayoutGrid
  badge?: number
}

/**
 * App-shell sidebar for the post-auth knowledge-base flow. Three states:
 *   - mobile  → rendered inside <Sheet>, full width, no collapse toggle
 *   - tablet  → 56px wide (icon-only), collapsed=true
 *   - desktop → 240px wide (label + icon + badge), collapsed=false
 *
 * Active route renders with a right-border accent stripe per DESIGN.md
 * §Sidebar. We use NavLink's render prop so the active style is driven by
 * react-router's match, not local component state — this avoids the
 * "click→state lag" feel during route transitions.
 */
export function Sidebar({
  collapsed,
  onToggleCollapse,
  hideCollapseToggle,
}: SidebarProps) {
  const startup = useAppStore((s) => s.startup)
  const membership = useAppStore((s) => s.membership)
  const openGapCount = useAppStore((s) => s.openGapCount)
  const { user, signOut } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const location = useLocation()

  const knowledgeNav: NavEntry[] = [
    { to: '/overview', label: 'Overview', icon: LayoutGrid },
    { to: '/documents', label: 'Documents', icon: FileText },
    {
      to: '/fill-gaps',
      label: 'Fill Gaps',
      icon: CircleHelp,
      badge: openGapCount > 0 ? openGapCount : undefined,
    },
  ]
  const distributionNav: NavEntry[] = [
    { to: '/export', label: 'Export', icon: Download },
  ]
  const settingsNav: NavEntry[] = [
    { to: '/settings', label: 'Settings', icon: Settings },
  ]

  const userInitials =
    user?.displayName
      ?.split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? 'U'

  const orgInitial = startup?.name?.[0]?.toUpperCase() ?? '·'
  const isLight = resolvedTheme === 'light'

  return (
    <aside
      className={cn(
        'flex flex-col h-screen shrink-0 bg-bg-primary border-r border-border-default',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-14' : 'w-60'
      )}
      aria-label="Primary navigation"
      data-testid="app-sidebar"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-14 shrink-0">
        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary text-text-on-accent font-display font-semibold text-sm">
          c
        </div>
        {!collapsed && (
          <span className="font-display font-semibold text-base text-text-primary tracking-tight">
            contxtmd
          </span>
        )}
      </div>

      {/* Org tile */}
      {startup && (
        <div
          className={cn(
            'mx-3 mb-3 rounded-md border border-border-default bg-bg-elevated',
            collapsed ? 'p-2' : 'p-3'
          )}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-md bg-primary/10 text-primary font-display font-semibold text-xs">
              {orgInitial}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-text-primary truncate">
                  {startup.name}
                </div>
                {membership && (
                  <div className="text-xs text-text-tertiary capitalize">
                    {membership.role}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2">
        <NavGroup label="Context Engine" collapsed={collapsed} entries={knowledgeNav} />
        <NavGroup label="Distribution" collapsed={collapsed} entries={distributionNav} />
        <NavGroup label="Account" collapsed={collapsed} entries={settingsNav} />
      </nav>

      {/* Footer: theme toggle + user row + sign out */}
      <div className="shrink-0 border-t border-border-default px-2 py-3 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setTheme(isLight ? 'dark' : 'light')}
          aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
          className={cn(
            'flex items-center gap-2 px-2 py-1.5 rounded-md',
            'text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors',
            collapsed && 'justify-center'
          )}
        >
          {isLight ? <Moon size={16} /> : <Sun size={16} />}
          {!collapsed && (
            <span className="text-sm">{isLight ? 'Dark mode' : 'Light mode'}</span>
          )}
        </button>

        <div
          className={cn(
            'flex items-center gap-2 px-2 py-1.5 rounded-md',
            collapsed && 'justify-center'
          )}
        >
          <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-primary/10 text-primary font-display font-semibold text-xs">
            {userInitials}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0 text-sm text-text-primary truncate">
              {user?.displayName ?? user?.email ?? 'You'}
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="Sign out"
              title="Sign out"
              className="text-text-tertiary hover:text-text-primary transition-colors p-1 rounded"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>

        {!hideCollapseToggle && onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex items-center gap-2 px-2 py-1.5 rounded-md',
              'text-text-tertiary hover:bg-bg-elevated hover:text-text-primary transition-colors',
              collapsed && 'justify-center'
            )}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            {!collapsed && <span className="text-xs">Collapse</span>}
          </button>
        )}
      </div>

      {/* Hidden helper so React Router considers `location` used in deps */}
      <span data-pathname={location.pathname} className="hidden" />
    </aside>
  )
}

interface NavGroupProps {
  label: string
  collapsed: boolean
  entries: NavEntry[]
}

function NavGroup({ label, collapsed, entries }: NavGroupProps) {
  return (
    <div className="mb-4">
      {!collapsed && (
        <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">
          {label}
        </div>
      )}
      {entries.map((entry) => (
        <NavItem key={entry.to} entry={entry} collapsed={collapsed} />
      ))}
    </div>
  )
}

interface NavItemProps {
  entry: NavEntry
  collapsed: boolean
}

function NavItem({ entry, collapsed }: NavItemProps) {
  const Icon = entry.icon
  return (
    <NavLink
      to={entry.to}
      title={collapsed ? entry.label : undefined}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 px-3 py-2 rounded-md text-sm relative',
          'transition-colors duration-150',
          collapsed && 'justify-center',
          isActive
            ? 'text-primary bg-primary/8'
            : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute right-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary"
            />
          )}
          <Icon size={18} className="shrink-0" />
          {!collapsed && <span className="flex-1 truncate">{entry.label}</span>}
          {!collapsed && entry.badge !== undefined && (
            <span
              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium bg-primary/12 text-primary"
              aria-label={`${entry.badge} open gap${entry.badge === 1 ? '' : 's'}`}
            >
              {entry.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}
